import {clsx} from 'clsx'
import {lazy, Suspense, useCallback, useRef, useState} from 'react'

import {SlidersIcon} from '@primer/octicons-react'
import {Button, Tooltip} from '@primer/react-brand/esm'

import type {ReactPartialAnchorProps} from '@github-ui/react-core/react-partial-anchor'
import type {ReactPartialAnchorElement} from '@github-ui/react-partial-anchor-element'

import styles from './HeaderAppearanceSettings.module.css'

// Code-split the appearance-settings dialog so its bundle (and `@github-ui/contrast-setting`) stays out
// of the initial marketing-header chunk that every cached, logged-out marketing page downloads.
const AppearanceSettings = lazy(async () => {
  const module = await import('@github-ui/appearance-settings-dialog')
  return {default: module.AppearanceSettings}
})

type HeaderAppearanceSettingsProps = {
  className?: string
}

/**
 * Appearance settings for logged-out users. This component owns the trigger button (the shared
 * `@github-ui/appearance-settings-dialog` attaches to an external anchor and renders no trigger of its
 * own), and lazy-loads the dialog so it stays out of the initial marketing-header bundle.
 *
 * The dialog mounts on pointer-enter / focus / pointer-down — all of which precede the activating click —
 * so its own click handler is wired up before the click lands, letting the first activation open it.
 */
export function HeaderAppearanceSettings({className}: HeaderAppearanceSettingsProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [reactPartialAnchor, setReactPartialAnchor] = useState<ReactPartialAnchorProps['reactPartialAnchor']>()

  const mountDialog = useCallback(() => {
    setReactPartialAnchor(previous => {
      if (previous || !anchorRef.current) return previous
      return {__wrapperElement: {anchor: anchorRef.current} as unknown as ReactPartialAnchorElement}
    })
  }, [])

  return (
    <>
      <Tooltip ref={anchorRef} text="Appearance settings" type="label">
        <Button
          type="button"
          variant="subtle"
          size="small"
          leadingVisual={<SlidersIcon />}
          aria-haspopup="dialog"
          className={clsx(styles.trigger, className)}
          onPointerEnter={mountDialog}
          onFocus={mountDialog}
          onPointerDown={mountDialog}
          onClick={mountDialog}
        />
      </Tooltip>

      {reactPartialAnchor ? (
        <Suspense fallback={null}>
          <AppearanceSettings reactPartialAnchor={reactPartialAnchor} singleMode />
        </Suspense>
      ) : null}
    </>
  )
}
